<template>
  <!-- OverlayLoader -->
  <OverlayLoader :show="showLoader" />
  <!-- Global loader -->
  <div v-if="loading" class="d-flex justify-content-center align-items-center h-full">
    <p><span class="loader">Loading...</span></p>
  </div>

  <!-- For authenticated users -->
  <!-- <div v-else-if="hasSession && hasLocation && locationsDropDown != null"> -->
  <div v-else-if="hasSession">
    <div v-if="$route.query.layout !== 'none' && $route.meta.layout != 'none' && $route.meta.layout != 'header'">
      <sidenav :custom_class="stColor" class="fixed-start" v-if="stShowSidenav" />
      <main class="main-content position-relative max-height-vh-100 h-100 overflow-x-hidden">
        <navbar :color="isAbsolute ? 'text-white opacity-8' : ''" v-if="stShowSidenav" :dropdownList="locationsDropDown"
          @selectedValue="updateSelectedLocation" :quote="quote" />
        <router-view />
        <app-footer v-show="showFooter" />
      </main>
    </div>

    <div v-else-if="$route.query.layout !== 'none' && $route.meta.layout == 'header'">
      <sidenav :custom_class="stColor" class="fixed-start" v-if="stShowSidenav" />
      <main class="main-content position-relative max-height-vh-100 h-100 overflow-x-hidden">
        <navbar class="shadow-xl border-1 border-bottom border-black" :color="isAbsolute ? 'text-white opacity-8' : ''"
          v-if="stShowSidenav" :dropdownList="locationsDropDown" @selectedValue="updateSelectedLocation"
          :quote="quote" />
        <router-view />
      </main>
    </div>

    <div v-else>
      <main class="main-content position-relative max-height-vh-100 h-100 overflow-x-hidden">
        <router-view />
      </main>
    </div>
  </div>

  <!-- ✅ For public (non-authenticated) routes -->
  <div v-else>
    <router-view />
  </div>

  <!-- Ezer AI Chatbot (available on all authenticated pages) -->
  <EzerModal v-if="hasSession" />

  <!-- Session timeout warning modal -->
  <div v-if="sessionWarning" class="session-warning-overlay">
    <div class="session-warning-modal">
      <div class="session-warning-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      </div>
      <h5 style="margin:0 0 8px;color:#1e293b;">Session Expiring Soon</h5>
      <p style="color:#64748b;font-size:14px;margin:0 0 16px;line-height:1.5;">
        Your session will expire in approximately <strong style="color:#f59e0b;">{{ sessionMinutesLeft }} minute(s)</strong> due to inactivity.
        Click below to stay logged in.
      </p>
      <div style="display:flex;gap:12px;justify-content:center;">
        <button @click="extendSession" style="padding:10px 24px;background:#16a34a;color:#fff;border:none;border-radius:8px;font-weight:600;cursor:pointer;">Stay Logged In</button>
        <button @click="logoutNow" style="padding:10px 24px;background:#e2e8f0;color:#475569;border:none;border-radius:8px;font-weight:600;cursor:pointer;">Log Out</button>
      </div>
    </div>
  </div>
</template>

<!-- Ask location if missing -->
<!-- <LocationModal v-if="hasSession && !hasLocation && locationsDropDown != null" :modalHeading="modalHeading"
    :dropdownList="locationsDropDown" @selectedValue="updateSelectedLocation" /> -->

<script setup>
import { ref, computed, watch, onMounted, onBeforeMount } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import Sidenav from "./components/Sidenav";
import Navbar from "@/components/Navbars/Navbar.vue";
import AppFooter from "@/components/Footer/Footer.vue";
// import LocationModal from "./components/Modal/LocationModal.vue";
import { getLocalStorage, setLocalStorage } from "@/utils/LocalStorageHandler.js";
import { GetToken } from "./utils/TokenHandler";
import OverlayLoader from "./components/Loader/OverlayLoader.vue";
import EzerModal from "@/components/ezer/EzerModal.vue";
import { useSessionTimeout } from "@/composables/useSessionTimeout.js";

// --- Core setup ---
const store = useStore();
const route = useRoute();
const router = useRouter();
// const modalHeading = ref("Select Location To Proceed");

// --- Session Timeout ---
const { showWarning: sessionWarning, minutesRemaining: sessionMinutesLeft, extendSession, expireSession: logoutNow } = useSessionTimeout();

// --- Reactive State ---
const hasSession = ref(false);
const hasLocation = ref(false);
const locationsDropDown = ref(null);
const quote = ref(null);
const showFooter = true;
const showLoader = ref(false);

// --- Vuex Computed ---
// const stSessionData = computed(() => store.getters.stSessionData);
const stLocations = computed(() => store.getters.stLocations);
const stShowSidenav = computed(() => store.getters.stShowSidenav);
const stColor = computed(() => store.getters.stColor);
const stQuoteData = computed(() => store.getters.stQuoteData);
const isAbsolute = computed(() => store.getters.stIsAbsolute);
const authToken = computed(() => store.getters.authToken);
const loading = ref(true);

// --- Vuex Actions/Mutations ---
const fetchDashboardData = (pid, lid) => store.dispatch("fetchDashboardData", { personid: pid, locationid: lid });
const fetchSessionData = () => store.dispatch("fetchSessionData");
const fetchPersonLocations = (pid) => store.dispatch("fetchPersonLocations", { personid: pid });
const setPersonLocations = (loc) => store.dispatch("setPersonLocations", loc);
const fetchInspirationalQuote = (lid) => store.dispatch("fetchInspirationalQuote", lid);
const fetchSidebarRoutes = (payload) => store.dispatch("fetchSidebarRoutes", payload);
const fetchlocationDetails = (lid) => store.dispatch("fetchlocationDetails", lid);
const fetchPendingRegistrations = (lid) => store.dispatch("fetchPendingRegistrations", lid);
const fetchMessagingYearlyTotals = (lid) => store.dispatch("fetchMessagingYearlyTotals", lid);
const fetchAutoEngine = (lid) => store.dispatch("fetchAutoEngine", lid);
const fetchUpcomingActivities = (lid) => store.dispatch("fetchUpcomingActivities", { locationid: lid, limit: 3 });
const fetchUserAssignments = (payload) => store.dispatch("fetchUserAssignments", payload);
const fetchSignupCount = (payload) => store.dispatch("fetchSignupCount", payload);
const fetchPermissionData = (payload) => store.dispatch("fetchPermissionData", payload);

// --- Methods ---
async function getLocationsData(personID) {
  await fetchPersonLocations(personID);
  locationsDropDown.value = stLocations.value;
}

async function getQuoteData(locationID) {
  await fetchInspirationalQuote(locationID);
  quote.value = stQuoteData.value || null;
}

async function loadDashboardAndCounts(personId, locationId) {
  // Validate required parameters
  if (!personId || !locationId) {
    console.error('loadDashboardAndCounts: Missing required parameters', { personId, locationId });
    return;
  }

  // First, fetch permission data and dashboard data to determine user access level
  const initialResults = await Promise.allSettled([
    fetchPermissionData({ personid: personId, locationid: locationId }),
    fetchDashboardData(personId, locationId),
    fetchUserAssignments({ personid: personId, locationid: locationId }),
    fetchSignupCount({ personid: personId, locationid: locationId }),
  ]);

  // Log any failures for initial API calls
  const initialApiNames = ['fetchPermissionData', 'fetchDashboardData', 'fetchUserAssignments', 'fetchSignupCount'];
  initialResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`API call failed: ${initialApiNames[index]}`, result.reason);
    }
  });
  
  // Get admin permissions from store
  const permissionData = store.getters.stPermissionData;
  const hasAdminAccess = permissionData?.adminMenuCards?.length > 0;

  // Only fetch admin-specific data if user has admin permissions
  if (hasAdminAccess) {
    const adminApiCalls = [
      fetchlocationDetails(locationId),
      fetchPendingRegistrations(locationId),
      fetchMessagingYearlyTotals(locationId),
      fetchAutoEngine(locationId),
      fetchUpcomingActivities(locationId)
    ];

    const adminResults = await Promise.allSettled(adminApiCalls);

    // Log any failures for admin API calls
    const adminApiNames = ['fetchlocationDetails', 'fetchPendingRegistrations', 'fetchMessagingYearlyTotals', 
                           'fetchAutoEngine', 'fetchUpcomingActivities'];
    adminResults.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`Admin API call failed: ${adminApiNames[index]}`, result.reason);
      }
    });
  }

  return initialResults;
}

async function getSessionData() {
  // Don't call APIs if we're on the SignIn page (user is selecting location)
  if (route.name === "SignIn" || route.path === "/sign-in") {
    return;
  }

  // Step 1: Save the user's selected locationid before fetching fresh session data.
  // We only preserve locationid (not the entire old session) to avoid stale data.
  const previousLocationId = getLocalStorage("sessionData")?.locationid;

  // Step 2: Clear session cache to force a fresh fetch from the API
  const sessionCacheKey = 'apiCache_getsessiondetails';
  localStorage.removeItem(sessionCacheKey);

  const session = await store.dispatch("initializeSession");

  await fetchSessionData();

  // Use the fresh API data; only fall back to the cached session if the fetch failed
  let updatedSession = store.getters.stSessionData || session;

  if (!updatedSession || updatedSession.error || updatedSession.message === "session not found") {
    router.push({ name: "SignIn" });
    return;
  }

  const personId = updatedSession.personId || updatedSession.personid;
  let locationid = updatedSession.locationid || updatedSession.locationId;

  // Step 3: If the API returned no locationid (it doesn't send the cookie/param),
  // restore the previously selected locationid so the user stays on their location.
  if (!locationid && previousLocationId) {
    console.log("API returned no locationid, restoring previous selection:", previousLocationId);
    locationid = String(previousLocationId);
    updatedSession = {
      ...updatedSession,
      locationid: locationid,
    };
    setLocalStorage("sessionData", updatedSession);
    store.commit("SET_SESSION_DATA", updatedSession);
  }

  if (!locationid) {
    if (!personId) {
      console.error('getSessionData: Missing personId in session', updatedSession);
      router.push({ name: "SignIn" });
      return;
    }
    await getLocationsData(personId);
    hasSession.value = true;
  } else {
    if (!personId) {
      console.error('getSessionData: Missing personId in session', updatedSession);
      router.push({ name: "SignIn" });
      return;
    }

    hasSession.value = true;
    hasLocation.value = true;

    await getLocationsData(personId);
    await getQuoteData(locationid);

    await fetchSidebarRoutes({ locationid });

    loadDashboardAndCounts(personId, locationid);
  }
}

async function updateSelectedLocation(location) {
  showLoader.value = true;
  try {
    const locationIdString = String(location);
    console.log("Updating location to:", locationIdString);

    // Step 1: Update location in backend session
    await setPersonLocations(locationIdString);
    console.log("Location set in backend");

    // Step 2: Wait a bit for backend to process the location change
    await new Promise(resolve => setTimeout(resolve, 200));

    // Step 3: Clear session data cache to force fresh fetch
    const sessionCacheKey = 'apiCache_getsessiondetails';
    localStorage.removeItem(sessionCacheKey);
    console.log("Cleared session data cache");

    // Step 4: Fetch fresh session data from backend (bypassing cache)
    await fetchSessionData();

    // Step 5: Get the updated session data from store
    let sessionData = store.getters.stSessionData || getLocalStorage("sessionData") || {};
    console.log("Session data after fetch:", sessionData);

    // Step 6: Verify and ensure locationid is correct
    // Use the location we just set, but also check what backend returned
    const backendLocationId = String(sessionData.locationid || sessionData.locationId || locationIdString);

    // Always use the location we just set to ensure consistency
    sessionData.locationid = locationIdString;
    if (sessionData.locationId) {
      delete sessionData.locationId; // Remove camelCase version
    }

    // Log if there's a mismatch
    if (backendLocationId !== locationIdString) {
      console.warn("Location mismatch! Backend returned:", backendLocationId, "Expected:", locationIdString);
      console.warn("Using expected location:", locationIdString);
    }

    // Step 7: Find the organization that contains this location
    let organizationid = null;
    const locations = stLocations.value;

    if (locations) {
      const locationList = Array.isArray(locations) ? locations : [locations];

      // Find the organization that contains the selected location
      const foundOrg = locationList.find(org =>
        org.locations?.some(loc => String(loc.location_id) === locationIdString)
      );

      if (foundOrg && foundOrg.organization_id) {
        organizationid = foundOrg.organization_id;
      }
    }

    // Step 8: Update sessionData with location and organization
    sessionData = {
      ...sessionData,
      locationid: locationIdString,
      organizationid: organizationid || sessionData.organizationid || null
    };

    console.log("Final session data to save:", sessionData);

    // Step 9: Persist to localStorage and Vuex
    // Make sure locationid is a string
    sessionData.locationid = String(locationIdString);
    setLocalStorage("sessionData", sessionData);
    store.commit("SET_SESSION_DATA", sessionData);

    // Step 10: Verify it was saved correctly - check multiple times
    let savedLocation = getLocalStorage("sessionData")?.locationid;
    let retryCount = 0;
    while (savedLocation !== String(locationIdString) && retryCount < 3) {
      console.warn(`Location save verification failed (attempt ${retryCount + 1}). Expected:`, locationIdString, "Got:", savedLocation);
      // Force it again
      sessionData.locationid = String(locationIdString);
      setLocalStorage("sessionData", sessionData);
      store.commit("SET_SESSION_DATA", sessionData);
      savedLocation = getLocalStorage("sessionData")?.locationid;
      retryCount++;
    }

    if (savedLocation === String(locationIdString)) {
      console.log("Location saved successfully and verified:", locationIdString);
    } else {
      console.error("CRITICAL: Failed to save location after multiple attempts! Expected:", locationIdString, "Got:", savedLocation);
    }

    hasSession.value = true;
    hasLocation.value = true;

    // Step 11: Clear ALL session-related caches before reload
    // This ensures fresh data is fetched on reload
    localStorage.removeItem(sessionCacheKey);
    console.log("Cleared session cache before reload");

    // Step 12: Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('locationUpdated'));

    // Step 13: Reload page to refresh all data with new location
    setTimeout(() => {
      window.location.reload();
    }, 300);
  }
  catch (error) {
    console.error("Error updating location:", error);
    window.$toast?.error("Failed to change location. Please try again.", {
      position: "top-right",
    });
  }
  finally {
    showLoader.value = false;
  }
}

async function checkSession() {
  const token = GetToken();
  if (!token) {
    console.warn("No token found — redirecting to login page");
    return false;
  }
  console.log("Token found — continuing session");
  return true;
}

// --- Watchers ---
watch(stLocations, (newVal) => {
  locationsDropDown.value = newVal;
});
watch(stQuoteData, (newVal) => {
  quote.value = newVal || null;
});

watch(
  authToken,
  async (token, previousToken) => {
    // Public/embeddable routes (e.g. /all-schedules/:locationid) must never
    // trigger authenticated bootstrap; a stale/expired token would 401 and
    // bounce the guest to the login page.
    if (token && token !== previousToken && !hasSession.value && !route.meta.public) {
      await getSessionData();
    }

    if (!token) {
      hasSession.value = false;
      hasLocation.value = false;
      locationsDropDown.value = null;
    }
  },
  { flush: "post" }
);

watch(
  () => route.name,
  async (routeName) => {
    if (
      !routeName ||
      route.meta.public ||
      routeName === "SignIn" ||
      routeName === "SignUp" ||
      !authToken.value ||
      hasSession.value
    ) {
      return;
    }

    await getSessionData();
  }
);

// --- Lifecycle Hooks ---
onBeforeMount(() => {
  store.state.isTransparent = "bg-transparent";
  const sidenav = document.getElementsByClassName("g-sidenav-show")[0];
  if (window.innerWidth > 1200) {
    sidenav.classList.add("g-sidenav-pinned");
  }
});

onMounted(async () => {
  await router.isReady();
  if (route.meta.public) {
    loading.value = false;
    return;
  }
  const hasValidSession = await checkSession();
  if (hasValidSession) {
    await getSessionData();
  } else {
    router.push({ name: "SignIn" });
  }
  loading.value = false;
});
</script>

<style scoped>
.loader {
  font-size: 48px;
  display: inline-block;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: bold;
  color: green;
  letter-spacing: 2px;
  position: relative;
}

.loader::after {
  content: "Loading";
  position: absolute;
  left: 0;
  top: 0;
  color: #fff;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
  animation: animloader 5s ease-in infinite;
}

@keyframes animloader {
  0% {
    width: 0%;
  }

  100% {
    width: 100%;
  }
}

.h-full {
  height: 100vh;
}

.session-warning-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.session-warning-modal {
  background: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}
.session-warning-icon {
  margin-bottom: 16px;
}
</style>

<style>
/* Global styles for readonly fields */
input[readonly],
textarea[readonly],
select[readonly] {
  background-color: #e9ecef !important;
  cursor: not-allowed;
}

/* Also handle disabled fields with gray background */
input:disabled,
textarea:disabled,
select:disabled {
  background-color: #e9ecef !important;
  cursor: not-allowed;
}
</style>